import type { ReactElement } from 'react';
import React from 'react';
import classNames from 'classnames';
import Markdown from 'markdown-to-jsx';
import styles from './ImageText.module.scss';
import Picture from '../../Picture/Picture';
import Source from '../../Picture/Source';
import { useWithStaticUrl } from '../../WebshopContext/WebshopContext';
import useTranslate from '../../Translation/hooks/UseTranslate';
import type { ImageText as ImageTextData } from '../ComponentTypes';
import { IMAGE_TEXT_LAYOUT_DARK, IMAGE_TEXT_LAYOUT_LIGHT } from '../ComponentTypes';

interface Props {
    component: ImageTextData;
    wrapper?: ReactElement;
}

const ImageText: React.FC<Props> = ({ component, wrapper }) => {
    const withStaticUrl = useWithStaticUrl();
    const reviewOne = useTranslate()('homepage', 'review_one');
    const reviewTwo = useTranslate()('homepage', 'review_two');
    const reviewThree = useTranslate()('homepage', 'review_three');
    const reviewFour = useTranslate()('homepage', 'review_four');
    const trimmedText = component.text.trim();

    if (component.layout === IMAGE_TEXT_LAYOUT_DARK) {
        return (
            <div className="section--xl">
                <div className={classNames(
                    styles.ImageText,
                    { [styles.ImageText_layout_dark as string]: component.layout === IMAGE_TEXT_LAYOUT_DARK },
                )}>
                    <div className={styles.ImageText__image}>
                        {component.image.imageUri && <Picture alt={component.image.alt || ''} imageUrl={withStaticUrl(component.image.imageUri)}>
                            <Source background={false} ratio={false} imageWidth={1600} minScreenWidth={1600} />
                            <Source background={false} ratio={false} imageWidth={1440} minScreenWidth={1440} />
                            <Source background={false} ratio={false} imageWidth={1200} minScreenWidth={1200} />
                            <Source background={false} ratio={false} imageWidth={768} />
                        </Picture> }
                    </div>
                    <div className={styles.ImageText__content}>
                        <div className={styles.ImageText__content__container}>
                            <div className="text-component lh-lg">
                                <div className="text-component__text">
                                    {wrapper
                                        ? React.cloneElement(wrapper, {}, trimmedText)
                                        : <Markdown options={{ disableParsingRawHTML: true }}>{trimmedText}</Markdown>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classNames(
            'section section--bg-grey homepage-reviews',
            { 'pt-0': component.layout === IMAGE_TEXT_LAYOUT_LIGHT },
        )}>
            <div className="wrapper">
                <div className={classNames(
                    styles.ImageText,
                    { [styles.ImageText_layout_light as string]: component.layout === IMAGE_TEXT_LAYOUT_LIGHT },
                )}>
                    <div className={styles.ImageText__content}>
                        <div className={styles.ImageText__content__container}>
                            <div className="text-component lh-lg">
                                <div className="text-component__text">
                                    {wrapper
                                        ? React.cloneElement(wrapper, {}, trimmedText)
                                        : <Markdown options={{ disableParsingRawHTML: true }}>{trimmedText}</Markdown>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.ImageText__image}>
                        <div className="review-list is-grid has-col-2">
                            {[
                                { name: 'Lara', text: reviewOne },
                                { name: 'Jeroen', text: reviewTwo },
                                { name: 'Samira', text: reviewThree },
                                { name: 'Tim', text: reviewFour },
                            ].map((review) => <div key={review.name} className="review">
                                <div className="review__rating-stars">
                                    <i className="fa-duotone fa-thin fa-star"></i>
                                    <i className="fa-duotone fa-thin fa-star"></i>
                                    <i className="fa-duotone fa-thin fa-star"></i>
                                    <i className="fa-duotone fa-thin fa-star"></i>
                                    <i className="fa-duotone fa-thin fa-star"></i>
                                </div>
                                <p className="review-text">
                                    {review.text}
                                </p>
                                <p className="review-name">
                                    {review.name}
                                </p>
                            </div>)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageText;
